import _ from 'lodash';
import React from 'react';

import { useArreteJournalierAutomatique } from '@/hooks';
import { useRegieStore } from '@/zustand';
import ArretePeriodiqueEditions from './Editions/ArretePeriodiqueEditions';
import ArretePeriodiqueListe from './Liste/ArretePeriodiqueListe';
import ArretePeriodiquePointage from './Pointage/ArretePeriodiquePointage';
import PointageEtEditionsWrapper from './PointageEtEditionsWrapper';

type StepType = 'liste' | 'pointage' | 'editions';

const ArretePeriodiqueSteps = () => {
  const [step, setStep] = React.useState<StepType>('liste');

  const regieIdentifiant = useRegieStore((state) => state.regie?.identifiant);
  // Clôture annuelle Cas 1 : ArreteAnnuelProcedureCreator crée un journalier antidaté au 31/12.
  // Arrêter la journée en cours ici daterait l'activité de décembre sur l'exercice suivant et
  // ferait échouer ce rattrapage (findDateFinAutomatique s'ancre sur le dernier journalier).
  const arreteAnnuelCas1 = useRegieStore(
    (state) => Boolean(state.regie?.arreteAnnuelRequis) && state.regie?.arreteAnnuelCas === 1,
  );

  // À l'entrée du menu, on force un arrêté journalier couvrant la JOURNÉE EN COURS
  // (`portee: 'jourEnCours'` → POST /arretes, `arr_date = now()`) : les reçus créés/annulés
  // aujourd'hui sont intégrés, ce que le mode automatique — plafonné à la veille — ne fait pas.
  // Déclenchement volontairement silencieux (pas de modale ici) : on n'exploite pas `arreteCreated`.
  const { run } = useArreteJournalierAutomatique({ mode: 'force', portee: 'jourEnCours' });
  // `run` change d'identité avec la régie ET le mandat : on le lit via une ref pour que l'effet
  // ne puisse pas être relancé pendant que l'appel est en vol (sinon le garde `hasRun` couperait
  // la ré-exécution et le déblocage du prévisionnel serait perdu définitivement).
  const runRef = React.useRef(run);
  runRef.current = run;

  const [journalierDone, setJournalierDone] = React.useState(false);
  const hasRun = React.useRef(false);

  React.useEffect(() => {
    if (_.isNil(regieIdentifiant) || hasRun.current) return; // une exécution par entrée de menu
    hasRun.current = true;

    if (arreteAnnuelCas1) {
      setJournalierDone(true); // rien à arrêter ici : la procédure annuelle s'en charge
      return;
    }

    (async () => {
      try {
        await runRef.current(); // attendre la fin avant de débloquer le prévisionnel
      } catch {
        // Le hook gère déjà le toast d'erreur : on ne laisse pas fuiter la promesse rejetée.
      } finally {
        setJournalierDone(true); // toujours débloqué : succès, 422 (rien à arrêter) ou erreur
      }
    })();
  }, [regieIdentifiant, arreteAnnuelCas1]);

  // Pas de régie sélectionnée ⇒ rien à attendre (évite un spinner sans fin côté liste).
  const journalierReady = journalierDone || _.isNil(regieIdentifiant);

  if (step === 'liste') {
    return (
      <ArretePeriodiqueListe
        onGoToPointage={() => setStep('pointage')}
        arreteJournalierReady={journalierReady}
      />
    );
  }

  // Étapes 2 et 3 : les contextes de règlements + la sélection API sont partagés par le wrapper.
  return (
    <PointageEtEditionsWrapper>
      {step === 'pointage' ? (
        <ArretePeriodiquePointage
          onGoToListe={() => setStep('liste')}
          onGoToEditions={() => setStep('editions')}
        />
      ) : (
        <ArretePeriodiqueEditions onGoToListe={() => setStep('liste')} />
      )}
    </PointageEtEditionsWrapper>
  );
};

export default ArretePeriodiqueSteps;
